import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  InputSignal,
  OnInit,
  OutputEmitterRef,
  Signal,
  WritableSignal,
  computed,
  effect,
  input,
  linkedSignal,
  output,
} from '@angular/core';

import { InsightsBaseEvent, insightsNamedEvent } from '@core/shared/util';
import { AttributeFacet, FacetDrilldown, FacetFeature, FacetFeatureValue } from '@mp/shared/facets/domain';
import { FacetExpansionStateService } from '@mp/shared/facets/util';

import { AttributeFacetBucketsGroupComponent } from './attribute-facet-buckets-group/attribute-facet-buckets-group.component';
import { AttributeFacetInsightsEventsConfig } from './attribute-facet-insights-config';

@Component({
  selector: 'mp-attribute-facet',
  templateUrl: './attribute-facet.component.html',
  styleUrl: './attribute-facet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AttributeFacetBucketsGroupComponent],
  providers: [FacetExpansionStateService],
})
export class AttributeFacetComponent implements OnInit {
  @HostBinding('class') readonly class = 'mp-attribute-facet';

  readonly facet: InputSignal<AttributeFacet> = input.required<AttributeFacet>();

  /**
   * If the current selection state should be stored, provide a unique key within the current instance
   * of `FacetSelectionService`. This is a constant value and must not be changed.
   */
  readonly selectionKey: InputSignal<string> = input.required<string>();

  /**
   * Specifies the name of the filter.
   */
  readonly filterName: InputSignal<string> = input<string>('');

  /**
   * Specifies the icon of the filter component.
   */
  readonly icon: InputSignal<string | undefined> = input<string>();

  /**
   * The placeholder of the search field.
   */
  readonly searchFieldPlaceholder: InputSignal<string> = input<string>('Durchsuchen');

  readonly trackInsightsEvents: InputSignal<boolean> = input<boolean>(false);

  /**
   * Emits on selection change and provides the selected feature values for use in the search request.
   */
  readonly changed: OutputEmitterRef<FacetFeatureValue[]> = output<FacetFeatureValue[]>();

  /**
   * Emits on expand/collapse/showMore and provides the corresponding feature along with its
   * optional expand limit in the form `<Feature>[:<ExpandLimit>]`. If expand limit is `0` the feature
   * is collapsed and supposed to be removed from the search request.
   */
  readonly drilldown: OutputEmitterRef<FacetDrilldown> = output<FacetDrilldown>();

  protected readonly insightsEventsConfig: Signal<AttributeFacetInsightsEventsConfig> = computed(() =>
    this.getFilterInsightsEventsConfig(this.filterName()),
  );

  protected readonly selectedValues: WritableSignal<Record<string, FacetFeatureValue[]>> = linkedSignal(() =>
    this.initSelectedValuesMap(),
  );

  constructor(
    private readonly facetExpansionStateService: FacetExpansionStateService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    effect(() => this.collapseUnselectedGroups());
  }

  ngOnInit(): void {
    this.facetExpansionStateService.setSelectionKey(this.selectionKey());
  }

  isExpanded({ value, selected }: AttributeFacet.BucketLevel0): boolean {
    return this.facetExpansionStateService.isExpanded(value, selected);
  }

  onIsExpandedChange(expanded: boolean, value: FacetFeature): void {
    this.facetExpansionStateService.setExpanded(expanded, value);

    this.drilldown.emit(
      expanded
        ? value
        : (`${value}:0` as FacetDrilldown) /*Storybook erkennt das nicht. Eigentlich kein Cast notwendig.*/,
    );
  }

  isSearchExpanded(value: FacetFeature): boolean {
    return this.facetExpansionStateService.isSearchExpanded(value);
  }

  onIsSearchExpandedChange(isSearchExpanded: boolean, value: FacetFeature): void {
    this.facetExpansionStateService.setSearchExpanded(isSearchExpanded, value);
  }

  onSelectedValuesChange(selectedValues: FacetFeatureValue[], groupLabel: string) {
    const selectedValuesMap: Record<string, FacetFeatureValue[]> = this.selectedValues();
    selectedValuesMap[groupLabel] = selectedValues;

    this.selectedValues.set(selectedValuesMap);

    this.changed.emit(this.getAllSelectedValues());
  }

  showMore(value: FacetFeature): void {
    this.drilldown.emit(`${value}:-1`);
  }

  private getAllSelectedValues(): FacetFeatureValue[] {
    return Object.values(this.selectedValues()).flat();
  }

  private collapseUnselectedGroups(): void {
    for (const b of this.facet().buckets) {
      // Expand buckets only when they are initially set and collapse them when they are not selected
      if (!b.selected || !this.facetExpansionStateService.isExpansionSet(b.value)) {
        this.facetExpansionStateService.setExpanded(b.selected, b.value);
      }
    }

    this.cdr.markForCheck();
  }

  private initSelectedValuesMap(): Record<string, FacetFeatureValue[]> {
    const selectedValuesMap: Record<string, FacetFeatureValue[]> = {};

    this.facet().buckets.forEach((bucket) => {
      selectedValuesMap[bucket.label] = this.getGroupSelectedValues(bucket.children);
    });

    return selectedValuesMap;
  }

  private getGroupSelectedValues(bucketChildren: readonly AttributeFacet.BucketLevel1<unknown>[]): FacetFeatureValue[] {
    return bucketChildren.filter((item) => item.selected).map(({ value }) => value);
  }

  private getFilterInsightsEventsConfig(filterName: string): AttributeFacetInsightsEventsConfig {
    return {
      filterExpand: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_EXPAND),
      filterCollapse: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_COLLAPSE),
      valueSelect: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_CHANGE),
      filterReset: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_RESET),
      groupSearch: insightsNamedEvent(filterName, InsightsBaseEvent.FILTER_SEARCH),
    };
  }
}
